// Landing page for organizations (businesses, schools, nonprofits, etc.)
import { Link, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import logo from '../../sidebar-logo.png';
import Chrome from '../../Assets/chrome.svg';
import Safari from '../../Assets/safari.svg';
import mixpanel from 'mixpanel-browser';
import {
  isAndroid,
  isIOS,
  isChrome,
} from 'react-device-detect';
import {
  Apple as AppleIcon,
  Android as AndroidIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  Handshake as HandshakeIcon,
  LocalLibrary as LibraryIcon,
  HealthAndSafety as HealthcareIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react';
import { logExperimentView, logConversion } from '../../firebase';
import { setLandingPage } from '../../utils/trackingParams';
import OneGiftLogo from '../../Components/OneGiftLogo';

import './landingA.scss';  // Reuse the same styles

const LandingOrganizations = observer(() => {
  useEffect(() => {
    mixpanel.track('Organizations Landing Page Visited', {
      page: 'landing_organizations'
    });
    setLandingPage('landing_organizations');
  }, []);

  const renderContactButton = () => {
    return (
      <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
        <a
          href="mailto:sales@audioread.com"
          className="cta-button"
          onClick={() => {
            mixpanel.track('Click Contact Sales', {
              placement: 'organizations_landing'
            });
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <div>Contact Sales</div>
          </div>
        </a>
      </div>
    );
  };

  const UseCaseCard = ({ icon, title, description }) => (
    <div style={{
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '12px',
      padding: '24px',
      width: '280px',
      margin: '12px',
      transition: 'transform 0.2s ease',
      cursor: 'pointer',
    }} className="use-case-card">
      {icon}
      <h3 style={{ marginTop: '16px', fontSize: '1.2em' }}>{title}</h3>
      <p style={{ fontSize: '0.9em', opacity: 0.8 }}>{description}</p>
    </div>
  );

  return (
    <div className="App">
      <header className="App-header" style={{ minHeight: '100vh' }}>
        <div className="nav-bar">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img className="audiblogs-logo" src={logo} alt="logo" />
          </Link>
        </div>

        <h1 className="bigger-headline" style={{ marginTop: '40px' }}>
          Audioread for Organizations
        </h1>

        <h2 className="subheadline">
          Empower your organization with AI-powered text-to-speech.
          <br />
          Perfect for businesses, schools, nonprofits, and communities.
        </h2>

        {renderContactButton()}

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: '1200px',
          margin: '48px auto'
        }}>
          <UseCaseCard
            icon={<BusinessIcon style={{ fontSize: '48px', color: '#4292fa' }} />}
            title="Businesses"
            description="Improve productivity and accessibility. Perfect for internal communications, documentation, and training materials."
          />
          <UseCaseCard
            icon={<SchoolIcon style={{ fontSize: '48px', color: '#8535cd' }} />}
            title="Educational Institutions"
            description="Support diverse learning styles and accessibility needs. Ideal for course materials, textbooks, and research papers."
          />
          <UseCaseCard
            icon={<HandshakeIcon style={{ fontSize: '48px', color: '#e82186' }} />}
            title="Nonprofits"
            description="Make your content accessible to everyone. Great for newsletters, reports, and educational materials."
          />
          <UseCaseCard
            icon={<GroupsIcon style={{ fontSize: '48px', color: '#ff5c57' }} />}
            title="Communities"
            description="Engage your audience in new ways. Perfect for newsletters, blogs, and community content."
          />
          <UseCaseCard
            icon={<LibraryIcon style={{ fontSize: '48px', color: '#00bcd4' }} />}
            title="Libraries"
            description="Make literature accessible to everyone. Ideal for digital collections, research materials, and reading programs."
          />
          <UseCaseCard
            icon={<HealthcareIcon style={{ fontSize: '48px', color: '#4caf50' }} />}
            title="Healthcare"
            description="Enhance patient education and accessibility. Perfect for medical documentation, patient resources, and training materials."
          />
        </div>

        <div style={{ maxWidth: '1000px', margin: '48px auto', padding: '0 24px' }}>
          <h2 style={{ 
            textAlign: 'center', 
            marginBottom: '40px',
            background: 'linear-gradient(135deg, #4292fa 0%, #8535cd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '2.5em'
          }}>Enterprise Features</h2>
          <div className="feature-cards-grid">
            <div className="feature-card" style={{
              background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.1) 0%, rgba(133, 53, 205, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}>
              <h3 style={{ color: '#4292fa', marginBottom: '16px' }}>Voice & Branding</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '12px' }}>✨ Custom voice options</li>
                <li style={{ marginBottom: '12px' }}>✨ Brand voice creation</li>
                <li style={{ marginBottom: '12px' }}>✨ Custom branding options</li>
              </ul>
            </div>
            
            <div className="feature-card" style={{
              background: 'linear-gradient(135deg, rgba(133, 53, 205, 0.1) 0%, rgba(232, 33, 134, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}>
              <h3 style={{ color: '#8535cd', marginBottom: '16px' }}>Team & Support</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '12px' }}>✨ Priority support</li>
                <li style={{ marginBottom: '12px' }}>✨ Team management</li>
                <li style={{ marginBottom: '12px' }}>✨ User role management</li>
              </ul>
            </div>

            <div className="feature-card" style={{
              background: 'linear-gradient(135deg, rgba(232, 33, 134, 0.1) 0%, rgba(255, 92, 87, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}>
              <h3 style={{ color: '#e82186', marginBottom: '16px' }}>Integration & API</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '12px' }}>✨ Custom integrations</li>
                <li style={{ marginBottom: '12px' }}>✨ API access</li>
                <li style={{ marginBottom: '12px' }}>✨ Webhook support</li>
              </ul>
            </div>

            <div className="feature-card" style={{
              background: 'linear-gradient(135deg, rgba(255, 92, 87, 0.1) 0%, rgba(66, 146, 250, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}>
              <h3 style={{ color: '#ff5c57', marginBottom: '16px' }}>Enterprise Benefits</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '12px' }}>✨ Volume discounts</li>
                <li style={{ marginBottom: '12px' }}>✨ Advanced analytics</li>
                <li style={{ marginBottom: '12px' }}>✨ SLA guarantees</li>
              </ul>
            </div>
          </div>
        </div>

        <div style={{ 
          marginTop: '80px',
          marginBottom: '80px',
          background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.1) 0%, rgba(133, 53, 205, 0.1) 100%)',
          padding: '60px 20px',
          borderRadius: '24px',
          maxWidth: '1000px',
          margin: '80px auto'
        }}>
          <h2 style={{
            fontSize: '2.5em',
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #4292fa 0%, #8535cd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Ready to transform your organization's content?</h2>
          <p style={{ fontSize: '1.3em', margin: '24px 0', opacity: 0.9 }}>
            Contact our sales team to learn more and get a personalized demo.
          </p>
          {renderContactButton()}
        </div>
      </header>

      <OneGiftLogo />
      <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/dmca">DMCA Policy</Link>
      </div>
      <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <a href="mailto:sales@audioread.com" target="_blank" rel="noopener noreferrer">
          Contact us at sales@audioread.com
        </a>
      </div>
      <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved.</div>
    </div>
  );
});

export default LandingOrganizations;
